import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { mdiPlus, mdiDelete } from '@mdi/js';
import { useApolloClient, useMutation } from '@apollo/client/react';

import { ITrainingSectionProps } from 'training/types';
import { Icon } from 'common/components/Icon';
import { PageTitle } from 'common/components/PageTitle';
import { Tooltip } from 'common/components/Tooltip';
import { ActionButton } from 'common/components/ActionButton';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useSelection } from 'common/components/Table';
import { TrainingGroupTable } from 'training/components/TrainingGroupTable';
import { useTrainingGroupList } from 'training/hooks/useTrainingGroupList';
import { useTrainingLinkedGroups } from 'training/hooks/useTrainingLinkedGroups';
import { getTrainingPermissions } from 'training/utils/permissions';
import { GroupSelectDrawer } from 'user/components/GroupSelectDrawer';
import { TGroupListItem } from 'user/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import {
    AddGroupsToTrainingDocument,
    RemoveGroupsFromTrainingDocument,
} from 'graphql/types';

interface IDeleteDialog {
    open: boolean;
    groupIds: string[];
}

export const Groups = ({ training }: ITrainingSectionProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const client = useApolloClient();
    const { canManageGroups = false } = getTrainingPermissions(training);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState<TGroupListItem[]>([]);
    const [deleteDialog, setDeleteDialog] = useState<IDeleteDialog>({
        open: false,
        groupIds: [],
    });

    const {
        groups,
        loading: groupsLoading,
        paginationSettings,
        searchValue,
        setPage,
        onSearch,
    } = useTrainingGroupList({ trainingId: training.id });

    const {
        linkedGroups,
        linkedGroupIds,
        loading: linkedGroupsLoading,
    } = useTrainingLinkedGroups({
        trainingId: training.id,
    });

    const selectionProps = useSelection(groups);

    const [addGroupsToTraining, { loading: addGroupsLoading }] = useMutation(
        AddGroupsToTrainingDocument
    );
    const [removeGroupsFromTraining, { loading: removeGroupsLoading }] =
        useMutation(RemoveGroupsFromTrainingDocument);

    const handleSelectGroup = (group: TGroupListItem) => {
        setSelectedGroups((prev) => [...prev, group]);
    };

    const handleRemoveSelectedGroup = (group: TGroupListItem) => {
        setSelectedGroups((prev) => prev.filter(({ id }) => id !== group.id));
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedGroups([]);
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({ open: false, groupIds: [] });
    };

    // Evict the group caches affected by add/remove: the paginated table list
    // and the training's `groups` field (powers the participant filter's
    // priority ordering via TrainingLinkedGroups).
    const evictGroupCaches = () => {
        client.cache.evict({ fieldName: 'paginatedGroups' });
        client.cache.evict({
            id: client.cache.identify({
                __typename: 'Training',
                id: training.id,
            }),
            fieldName: 'groups',
        });
        client.cache.gc();
    };

    const handleAddGroups = async (added?: TGroupListItem[]) => {
        if (!added?.length) {
            handleCloseDrawer();

            return;
        }

        const groupIds = added.map(({ id }) => id);

        try {
            const response = await addGroupsToTraining({
                variables: {
                    trainingId: training.id,
                    groupIds,
                },
            });

            const { succeededIds, failedIds } =
                response.data?.addGroupsToTraining?.bulkAdd || {};

            if (succeededIds?.length) {
                const names = added
                    .filter(({ id }) => succeededIds.includes(id))
                    .map(({ name }) => name)
                    .join(', ');

                displaySnackbar(
                    translate('trainingGroups.groupAddedSuccess', {
                        groups: names,
                        count: succeededIds.length,
                        interpolation: { escapeValue: false },
                    }),
                    { variant: 'success' }
                );

                evictGroupCaches();
            }

            if (failedIds?.length) {
                const names = added
                    .filter(({ id }) => failedIds.includes(id))
                    .map(({ name }) => name)
                    .join(', ');

                displaySnackbar(
                    translate('trainingGroups.groupAddedFailed', {
                        groups: names,
                        count: failedIds.length,
                        interpolation: { escapeValue: false },
                    }),
                    { variant: 'error' }
                );
            }

            handleCloseDrawer();
        } catch (error) {
            showApolloError(error);
        }
    };

    const handleRemoveGroups = async () => {
        if (!deleteDialog.groupIds.length) return;

        const groupIds = deleteDialog.groupIds;
        // Resolve names from the full linked set: the selection can span
        // multiple pages, while `groups` only holds the current page.
        const groupsBeingRemoved = linkedGroups.filter((group) =>
            groupIds.includes(group.id)
        );

        try {
            const response = await removeGroupsFromTraining({
                variables: {
                    trainingId: training.id,
                    groupIds,
                },
            });

            const { succeededIds, failedIds } =
                response.data?.removeGroupsFromTraining?.bulkRemove || {};

            if (succeededIds?.length) {
                const names = groupsBeingRemoved
                    .filter(({ id }) => succeededIds.includes(id))
                    .map(({ name }) => name)
                    .join(', ');

                displaySnackbar(
                    translate('trainingGroups.groupRemovedSuccess', {
                        groups: names,
                        count: succeededIds.length,
                        interpolation: { escapeValue: false },
                    }),
                    { variant: 'success' }
                );

                evictGroupCaches();
            }

            if (failedIds?.length) {
                const names = groupsBeingRemoved
                    .filter(({ id }) => failedIds.includes(id))
                    .map(({ name }) => name)
                    .join(', ');

                displaySnackbar(
                    translate('trainingGroups.groupRemovedFailed', {
                        groups: names,
                        count: failedIds.length,
                        interpolation: { escapeValue: false },
                    }),
                    { variant: 'error' }
                );
            }

            selectionProps.setSelected([]);
            closeDeleteDialog();
        } catch (error) {
            showApolloError(error);
        }
    };

    const loading = groupsLoading || addGroupsLoading || removeGroupsLoading;

    const tableActions = canManageGroups
        ? [
              {
                  tooltipTitle: translate('trainingGroups.removeFromTraining'),
                  iconPath: mdiDelete,
                  onClick: (selectedGroupIds: string[]) => {
                      setDeleteDialog({
                          open: true,
                          groupIds: selectedGroupIds,
                      });
                  },
              },
          ]
        : [];

    return (
        <>
            <PageTitle>
                {`${translate('groups')} - ${training.title}`}
            </PageTitle>
            {canManageGroups && (
                <Box sx={{ display: 'flex', mb: 1.5, mr: 1 }}>
                    <Tooltip
                        placement="bottom"
                        title={translate('trainingGroups.addTrainingGroup')}
                    >
                        <Box>
                            <ActionButton
                                outlined
                                aria-label={translate(
                                    'trainingGroups.addTrainingGroup'
                                )}
                                disabled={linkedGroupsLoading}
                                size="medium"
                                onClick={() => setDrawerOpen(true)}
                            >
                                <Icon path={mdiPlus} />
                            </ActionButton>
                        </Box>
                    </Tooltip>
                </Box>
            )}
            <TrainingGroupTable
                groups={groups}
                isEditable={canManageGroups}
                loading={loading}
                paginationProps={{ paginationSettings, setPage }}
                searchValue={searchValue}
                selectionProps={selectionProps}
                tableActions={tableActions}
                trainingId={training.id}
                onSearch={onSearch}
            />
            {canManageGroups && (
                <GroupSelectDrawer
                    addedGroups={[]}
                    disabledGroupIds={linkedGroupIds}
                    open={drawerOpen}
                    selectedGroups={selectedGroups}
                    setSelectedGroups={setSelectedGroups}
                    onAddGroups={handleAddGroups}
                    onClose={handleCloseDrawer}
                    onRemoveGroup={handleRemoveSelectedGroup}
                    onSelectGroup={handleSelectGroup}
                />
            )}
            <ConfirmDialog
                color="error"
                confirmText={translate('delete')}
                loading={removeGroupsLoading}
                open={deleteDialog.open}
                title={translate(
                    'trainingGroups.deleteTrainingGroupMessage.title'
                )}
                onCancel={closeDeleteDialog}
                onClose={closeDeleteDialog}
                onConfirm={handleRemoveGroups}
            >
                {translate('trainingGroups.deleteTrainingGroupMessage.text')}
            </ConfirmDialog>
        </>
    );
};
